import React, { useEffect, useState } from "react";
import { formatMessage, formatMessageWithParams } from "../../utils/helpers/LanguageHelper";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import FooterPanel from "../layout/FooterPanel";
import { AdvancedDocumentPanel } from "./AdvancedDocumentPanel";
import { InfoDocumentPanel } from "./InfoDocumentPanel";
import { PivotObject } from "../generic/PivotObject";
import { ObjectPanelTabs } from "../coobjects/co-panel/ObjectPanelComponent";
import { DocumentParameters } from "./document-parameters/DocumentParameters";
import { checkForDuplicate } from "../../api/document/Documents";
import { normalizeDocumentName } from "../../utils/helpers/ObjectCheckerHelper";
import { normalizePath } from "../../redux/feature/document/document-slice";
import { CustomModal } from "../layout/ActionModal";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import { DimensionParameter } from "../../api/models/dimension-parameter";
import { CoObjectAdvanced } from "../interfaces/CoObject";
import LoadingManager from "../../common/loading/LoadingManager";
import { DocumentPartSettings } from "../../api/models/document-part-settings";
import { deleteDocumentFromRagIndex, prepareRagIndex } from "../../api/document/RagIndexing";
import type { DocumentData, EditOperationType } from "./document-types";

export type { DocumentData, EditOperationType };

interface EditDocumentProps {
  onLastBackButton: () => void | undefined;
  onBackButton?: () => void;
  isEdit: boolean;
  readonly?: boolean;
  editDocumentOperationType?: EditOperationType;
  onSave: (state: DocumentData, mustAlignChildren: boolean) => Promise<void>;
  fatherPartId?: string;
  getDocumentPartDetails: () => Promise<{
    documentData: DocumentData;
    sourceDocumentName?: string;
  }>;
  onUpdateDocumentInfo?: (infoDocState: DocumentData) => void;
  onTabChange?: (selectedTabIndex: string) => void;
}

interface EditDocumentState {
  key: string;
  tabs: ObjectPanelTabs[];
  documentData: DocumentData;
  isModified: boolean;
  duplicateDocumentsNames: string[];
  sourceDocumentName?: string;
  askForAttachmentsDuplication?: boolean;
  mustAlignChildren?: boolean;
  mustUpdateDocumentToFab?: boolean;
}

const getTabsList = (
  isEdit: boolean,
  isRoot: boolean,
  isMultilinked: boolean,
  operation: EditOperationType
): ObjectPanelTabs[] => {
  let tabs = [
    {
      key: "1",
      name: "cof.documentproperties.generalinfo",
      isValid: isEdit || operation === "IMPORTNONTAGETIK",
    },
    {
      key: "2",
      name: "cof.createnewobject.parameters",
      isValid: isEdit,
    },
  ];

  if (isRoot || isMultilinked) {
    tabs.push({
      key: "3",
      name: "cof.createnewobject.advanced",
      isValid: true,
    });
  }
  return tabs;
};

const isSaveButtonDisabled = (tabs: ObjectPanelTabs[], isModified: boolean, editOperationType?: EditOperationType) => {
  // TODO check this, can be wrong...
  if (!isModified && editOperationType === "DETACH") return false;
  return !(
    isModified &&
    tabs.reduce<boolean>((acc, tab) => {
      return acc && tab.isValid;
    }, true)
  ); //all tab valid
};

const isNextButtonDisabled = (tabs: ObjectPanelTabs[], currentKey: string) => {
  const thisTab = tabs.find((tab) => tab.key === currentKey);
  return !thisTab?.isValid;
};
const isSaveButtonVisible = (tabs: ObjectPanelTabs[], currentKey: string, isEdit: boolean) => {
  const lastTab = tabs[tabs.length - 1];
  return isEdit || lastTab?.key === currentKey; //if we're at the last tab then next is not available
};

const isOnFirstTab = (tabs: ObjectPanelTabs[], currentKey: string) => {
  const firstTab = tabs[0];
  return firstTab?.key === currentKey;
};

export const EditDocument: React.FunctionComponent<EditDocumentProps> = (props) => {
  const [state, setState] = useState<EditDocumentState>({
    key: "1",
    documentData: null,
    tabs: [],
    isModified: false,
    duplicateDocumentsNames: [],
    mustAlignChildren: false,
    mustUpdateDocumentToFab: false,
  });
  useEffect(() => {
    LoadingManager.create("getInitialDocumentPartDetails");
    getInitialDocumentPartDetails().finally(() => LoadingManager.remove("getInitialDocumentPartDetails"));
  }, []);

  async function getInitialDocumentPartDetails() {
    const { documentData, sourceDocumentName } = await props.getDocumentPartDetails();
    const askForAttachmentsDuplication = props.editDocumentOperationType === "DUPLICATE";

    setState((s) => ({
      ...s,
      isModified: !props.isEdit,
      documentData,
      sourceDocumentName,
      tabs: getTabsList(props.isEdit, documentData.root, documentData.multilinked, props.editDocumentOperationType),
      askForAttachmentsDuplication,
    }));
  }

  function onClickPivot(selected: { props: { itemKey: string } }) {
    setState((s) => ({ ...s, key: selected.props.itemKey }));
    props.onTabChange?.(selected.props.itemKey);
  }

  function updateDocumentInfo(infoDocState: DocumentData, isValid: boolean) {
    if (props.readonly) return;
    setState((s) => {
      const newTabs: ObjectPanelTabs[] = [...s.tabs];
      newTabs[0].isValid = isValid;
      props.onUpdateDocumentInfo?.(infoDocState);
      return {
        ...s,
        documentData: {
          ...s.documentData,
          language: infoDocState.language,
          name: infoDocState.name,
          path: infoDocState.path,
          description: infoDocState.description,
          duplicateAttachments: infoDocState.duplicateAttachments,
          copySharePointUserRights: infoDocState.copySharePointUserRights,
        },
        tabs: newTabs,
        isModified: true,
        mustAlignChildren:
          s.documentData.language != infoDocState.language || s.documentData.unaligned || s.mustAlignChildren,
      };
    });
  }

  function updateDimensionParameters(parameters: DimensionParameter[], isValid: boolean, isModified: boolean) {
    if (props.readonly) return;
    setState((s) => {
      const newTabs: ObjectPanelTabs[] = [...s.tabs];
      newTabs[1].isValid = isValid;
      return {
        ...s,
        documentData: {
          ...s.documentData,
          parameters: parameters,
        },
        tabs: newTabs,
        isModified: isModified || s.isModified,
        mustAlignChildren: true,
      };
    });
  }

  function updateDocumentAdvanced(
    advancedDocState: CoObjectAdvanced,
    isValid: boolean,
    mustAlignChildren: boolean = true
  ) {
    if (props.readonly) return;
    setState((s) => {
      const newTabs: ObjectPanelTabs[] = [...s.tabs];
      newTabs[2].isValid = isValid;
      return {
        ...s,
        documentData: {
          ...s.documentData,
          advanced: advancedDocState,
        },
        tabs: newTabs,
        isModified: true,
        mustAlignChildren: mustAlignChildren || s.mustAlignChildren,
      };
    });
  }

  function updateDocumentSettings(docSettingsState: DocumentPartSettings, mustUpdateDocumentToFab?: boolean) {
    if (props.readonly) return;
    setState((s) => {
      return {
        ...s,
        documentData: {
          ...s.documentData,
          settings: docSettingsState,
        },
        isModified: true,
        mustUpdateDocumentToFab: mustUpdateDocumentToFab ?? s.mustUpdateDocumentToFab,
      };
    });
  }

  const getPanel = () => {
    switch (state.key) {
      case "1":
        return (
          <InfoDocumentPanel
            documentInfo={state.documentData}
            updateDocumentInfo={updateDocumentInfo}
            isEdit={props.isEdit}
            readonly={props.readonly}
            editDocumentOperationType={props.editDocumentOperationType}
            removeLocale={!state.documentData?.root && !state.documentData?.multilinked}
            isDuplicate={props.editDocumentOperationType === "DUPLICATE"}
            askForAttachmentsDuplication={state.askForAttachmentsDuplication}
          />
        );
      case "2":
        return (
          <DocumentParameters
            canInherit={!state.documentData?.root && !state.documentData.multilinked}
            parameters={state.documentData.parameters ?? []}
            inheritableParameters={state.documentData.inheritableParameters ?? []}
            updateParameters={updateDimensionParameters}
            readonly={props.readonly}
          />
        );
      case "3":
        return (
          <AdvancedDocumentPanel
            isRoot={state.documentData.root}
            documentId={state.documentData.documentId}
            documentName={state.documentData.name}
            documentPath={state.documentData.path}
            isDuplicate={props.editDocumentOperationType === "DUPLICATE"}
            documentAdvanced={state.documentData.advanced}
            updateDocumentAdvanced={updateDocumentAdvanced}
            documentSettings={state.documentData.settings}
            updateDocumentSettings={updateDocumentSettings}
            isEdit={props.isEdit}
            readonly={props.readonly}
            parameters={state.documentData.parameters}
            goToParametersPage={onBackButtonClicked}
          />
        );
      default:
        return null;
    }
  };

  const keepParametersWithDimensionAndValue = (parameters: DimensionParameter[]) => {
    return parameters?.reduce<DimensionParameter[]>((acc, el) => {
      const dimensions = el.dimensions.reduce((dims, dim) => {
        if (dim.values.length) return [...dims, dim];
        return dims;
      }, []);
      if (dimensions.length) return [...acc, { ...el, dimensions }];
      return acc;
    }, []);
  };

  const onSaveButton = async () => {
    if (props.readonly) return;
    const filteredParameters = keepParametersWithDimensionAndValue(state.documentData.parameters);
    const advanced = state.documentData.advanced;
    if (!advanced.language) advanced.language = state.documentData.language;
    if (!advanced.templateInfo || advanced.templateInfo.templateId == "default") advanced.templateInfo = null;

    await props.onSave(
      {
        ...state.documentData,
        advanced,
        parameters: filteredParameters,
        fatherPartId: props.fatherPartId ? props.fatherPartId : "root",
      },
      state.documentData.hasChildren && state.mustAlignChildren
    );

    await getInitialDocumentPartDetails();
    if (state.mustUpdateDocumentToFab) {
      if (state.documentData.settings.isRagIndexingEnabled) {
        try {
          await prepareRagIndex(state.documentData.documentId);
        } catch (error) {
          console.error("[EditDocument] Failed to prepare RAG index:", error);
        }
      } else {
        await deleteDocumentFromRagIndex(state.documentData.documentId);
      }
    }
  };

  if (state.duplicateDocumentsNames?.length) {
    const titleId =
      props.editDocumentOperationType === "DUPLICATE"
        ? "cof.documentmanagement.outline.duplicate.tile.duplicatedocument"
        : "cof.documentmanagement.outline.detach.tile.detachdocument";

    return (
      <CustomModal
        modalTitle={formatMessage(titleId) + (state.sourceDocumentName ? ` - ${state.sourceDocumentName}` : "")}
        messageTitle={formatMessage("cof.documentmanagement.outline.duplicate.tile.alreadyexists")}
        message={formatMessageWithParams("cof.documentmanagement.outline.duplicate.message.alreadyexists", {
          documentName: "<br/>" + state.duplicateDocumentsNames.join(";<br/>") + ".<br/>",
        })}
        type="confirm"
        onHide={() => {}}
        actions={[
          {
            title: formatMessage("cof.base.edit"),
            onClick: () => {
              setState((s) => ({
                ...s,
                duplicateDocumentsNames: [],
              }));
            },
            variant: "white",
          },
        ]}
      />
    );
  }

  const getSaveButtonText = () => {
    if (props.editDocumentOperationType === "DUPLICATE") return "cof.base.rollforward";
    if (props.editDocumentOperationType === "DETACH") return "cof.base.detach";
    if (props.isEdit) return "cof.base.save";
    return "cof.base.create";
  };

  const getSaveButtonId = () => {
    if (props.editDocumentOperationType === "DUPLICATE") return "duplicate_button";
    if (props.editDocumentOperationType === "DETACH") return "detach_button";
    if (props.isEdit) return "save_button";
    return "create_button";
  };

  const onNextButtonClick = async () => {
    let duplicateDocumentsNames = [];

    if (
      (props.editDocumentOperationType === "DETACH" || props.editDocumentOperationType === "DUPLICATE") &&
      isOnFirstTab(state.tabs, state.key)
    ) {
      duplicateDocumentsNames = await checkForDuplicate({
        documentId: state.documentData.documentId,
        documentName: normalizeDocumentName(state.documentData.name),
        path: normalizePath(state.documentData.path),
      });
    }

    let newTabKey: string;

    setState((s) => {
      newTabKey = duplicateDocumentsNames.length ? s.key : (Number(s.key) + 1).toString();
      return { ...s, key: newTabKey, duplicateDocumentsNames: duplicateDocumentsNames ?? [] };
    });
    props.onTabChange?.(newTabKey);
  };

  const onBackButtonClicked = () => {
    if (props.onBackButton) {
      props.onBackButton();
      return;
    }
    if (isOnFirstTab(state.tabs, state.key)) {
      props.onLastBackButton();
      return;
    }
    let newTabKey: string;
    setState((s) => {
      newTabKey = (Number(s.key) - 1).toString();
      return { ...s, key: newTabKey };
    });
    props.onTabChange?.(newTabKey);
  };

  return (
    <div
      className="ms-Grid"
      dir="ltr"
      style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}
    >
      <div className="ms-Grid-row" style={{ flex: "0 0 auto" }}>
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
          <PivotObject
            isEdit={props.isEdit}
            selectedKey={Number(state.key)}
            onClickPivot={onClickPivot}
            items={state.tabs}
            defaultTabName={formatMessage("cof.documentproperties.generalinfo")}
          />
        </div>
      </div>
      <div className="ms-Grid-row" style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", paddingBottom: "70px" }}>
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">{getPanel()}</div>
      </div>
      <FooterPanel
        scrollViewNavigationProps={
          !props.isEdit && state.tabs.length > 1
            ? { activeLink: state.key.toString(), items: state.tabs, isClickEnabled: false }
            : undefined
        }
      >
        {(!isOnFirstTab(state.tabs, state.key) || props.onLastBackButton) && (
          <PrimaryButton
            id="previous_button"
            data-testid="Back"
            className={ButtonVariants.white}
            text={formatMessage("cof.base.previous")}
            onClick={onBackButtonClicked}
            style={{ float: "left" }}
          />
        )}
        {isSaveButtonVisible(state.tabs, state.key, props.isEdit) ? (
          <PrimaryButton
            id={getSaveButtonId()}
            data-testid="Save"
            className={ButtonVariants.primary}
            disabled={
              !!props.readonly || isSaveButtonDisabled(state.tabs, state.isModified, props.editDocumentOperationType)
            }
            text={formatMessage(getSaveButtonText())}
            onClick={onSaveButton}
            style={{ float: "right" }}
          />
        ) : (
          <PrimaryButton
            id="next_button"
            data-testid="Next"
            className={ButtonVariants.primary}
            text={formatMessage("cof.base.next")}
            onClick={onNextButtonClick}
            disabled={isNextButtonDisabled(state.tabs, state.key)}
            style={{
              float: "right",
            }}
          />
        )}
      </FooterPanel>
    </div>
  );
};
